<template>
  <div id="survey-result-preview">
    <v-dialog v-model="showDialog" max-width="780px" scrollable>
      <v-card class="rounded-24 overflow-hidden modal-card" v-if="previewItem">
        <!-- JCI Brand Header -->
        <div
          class="modal-header px-6 py-4 d-flex justify-space-between align-center"
        >
          <div class="d-flex align-center">
            <div class="header-icon-box mr-3">
              <v-icon color="#3A67B1" size="20"
                >mdi-clipboard-text-outline</v-icon
              >
            </div>
            <div>
              <h3
                class="text-white font-weight-bold mb-0 text-h6"
                style="font-size: 1.15rem !important; line-height: 1.2"
              >
                Survey Response Record
              </h3>
              <span
                class="small"
                style="font-size: 0.78rem; color: rgba(255, 255, 255, 0.9)"
              >
                Detailed submission breakdown
              </span>
            </div>
          </div>
          <v-btn icon dark small @click="closeDialog" class="close-btn">
            <v-icon size="20" color="#FFFFFF">mdi-close</v-icon>
          </v-btn>
        </div>

        <!-- Scrollable Content Body -->
        <v-card-text class="pa-6 modal-body">
          <!-- Respondent Hero Card -->
          <div
            class="respondent-hero-card pa-5 rounded-20 mb-6 text-white position-relative overflow-hidden"
          >
            <div class="hero-bg-accent"></div>
            <div
              class="d-flex align-center flex-wrap justify-space-between position-relative"
              style="z-index: 2"
            >
              <div class="d-flex align-center">
                <div class="avatar-ring mr-4 flex-shrink-0">
                  <v-avatar color="#FFFFFF" size="52">
                    <span
                      class="font-weight-bold text-h6"
                      style="color: #3a67b1 !important"
                    >
                      {{ getInitials(previewItem.RespondentTxt) }}
                    </span>
                  </v-avatar>
                </div>
                <div>
                  <div
                    class="font-weight-bold text-white mb-1"
                    style="font-size: 1.1rem; letter-spacing: -0.2px"
                  >
                    {{ previewItem.RespondentTxt }}
                  </div>
                  <div class="d-flex align-center flex-wrap" style="gap: 12px">
                    <span class="hero-subtext" v-if="previewItem.ResponseEmail">
                      <v-icon size="14" color="#FFFFFF" class="mr-1"
                        >mdi-email-outline</v-icon
                      >
                      {{ previewItem.ResponseEmail }}
                    </span>
                  </div>
                </div>
              </div>

              <div class="mt-3 mt-sm-0">
                <div
                  class="time-badge px-3 py-2 rounded-xl d-flex align-center"
                >
                  <v-icon size="16" color="#56BDA3" class="mr-2"
                    >mdi-calendar-check</v-icon
                  >
                  <div>
                    <div
                      style="
                        font-size: 0.68rem;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                        color: rgba(255, 255, 255, 0.85);
                      "
                    >
                      Submitted
                    </div>
                    <div
                      class="font-weight-bold"
                      style="font-size: 0.82rem; color: #ffffff"
                    >
                      {{ previewItem.SubmittedAtTxt || "-" }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Section Label -->
          <div class="d-flex align-center justify-space-between mb-4">
            <div class="d-flex align-center">
              <span class="section-indicator mr-2"></span>
              <h4
                class="font-weight-bold mb-0"
                style="font-size: 0.95rem; color: #0a0f29"
              >
                Questions & Answers
              </h4>
            </div>
            <v-chip
              x-small
              color="#EBF4FB"
              text-color="#3A67B1"
              class="font-weight-bold px-2 border-navy"
            >
              {{ questions.length }} Questions
            </v-chip>
          </div>

          <!-- Vertical Timeline Feed -->
          <div class="timeline-feed px-2">
            <div
              v-for="(q, idx) in questions"
              :key="q.QuestionId"
              class="timeline-item position-relative mb-5 pl-8"
            >
              <!-- Timeline Node Circle -->
              <div class="timeline-node">
                {{ formatIndex(idx + 1) }}
              </div>

              <!-- Question & Answer Card -->
              <div class="qa-card rounded-20 pa-4 border bg-white">
                <div class="font-weight-bold mb-3 question-text">
                  {{ q.QuestionText }}
                </div>

                <!-- Answer Content Box -->
                <div class="answer-wrapper">
                  <!-- File Upload Answer -->
                  <template v-if="isFileType(q.QuestionType)">
                    <div
                      v-if="previewItem['Q' + q.QuestionId]"
                      class="file-download-card pa-3 rounded-16 border d-flex align-center justify-space-between"
                    >
                      <div class="d-flex align-center overflow-hidden mr-2">
                        <div class="file-icon-box mr-3 flex-shrink-0">
                          <v-icon color="#3A67B1" size="22"
                            >mdi-file-download-outline</v-icon
                          >
                        </div>
                        <span
                          class="file-name text-truncate font-weight-medium"
                          style="font-size: 0.85rem; color: #414042"
                        >
                          {{ previewItem["Q" + q.QuestionId] }}
                        </span>
                      </div>
                      <v-btn
                        small
                        rounded
                        color="#3A67B1"
                        class="text-white text-capitalize elevation-0 flex-shrink-0"
                        :href="fileBaseUrl + previewItem['Q' + q.QuestionId]"
                        target="_blank"
                        rel="noopener"
                      >
                        <v-icon left small color="#FFFFFF"
                          >mdi-open-in-new</v-icon
                        >
                        View File
                      </v-btn>
                    </div>
                    <div v-else class="empty-answer-box pa-3 rounded-16">
                      <v-icon size="16" color="#3A67B1" class="mr-1"
                        >mdi-paperclip-off</v-icon
                      >
                      <span class="font-italic small" style="color: #414042"
                        >No file attached</span
                      >
                    </div>
                  </template>

                  <!-- Rating / Scale / Short Answer -->
                  <template
                    v-else-if="isShortAnswer(previewItem['Q' + q.QuestionId])"
                  >
                    <div
                      class="short-answer-pill d-inline-flex align-center px-4 py-2 rounded-xl"
                    >
                      <v-icon size="16" color="#56BDA3" class="mr-2"
                        >mdi-check-circle</v-icon
                      >
                      <span
                        class="font-weight-bold"
                        style="font-size: 0.9rem; color: #0a0f29"
                      >
                        {{ previewItem["Q" + q.QuestionId] }}
                      </span>
                    </div>
                  </template>

                  <!-- Text / Long Answer -->
                  <template v-else>
                    <div
                      v-if="previewItem['Q' + q.QuestionId]"
                      class="text-answer-card pa-4 rounded-16"
                    >
                      <div class="quote-mark">“</div>
                      <div class="answer-body-text">
                        {{ previewItem["Q" + q.QuestionId] }}
                      </div>
                    </div>
                    <div v-else class="empty-answer-box pa-3 rounded-16">
                      <v-icon size="16" color="#3A67B1" class="mr-1"
                        >mdi-minus-circle-outline</v-icon
                      >
                      <span class="font-italic small" style="color: #414042"
                        >No response provided</span
                      >
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </v-card-text>

        <!-- Modern Footer -->
        <v-card-actions
          class="pa-4 px-6 justify-space-between bg-white border-top"
        >
          <span class="small" style="font-size: 0.78rem; color: #414042">
            Survey Response Record
          </span>
          <v-btn
            color="#3A67B1"
            class="rounded-xl text-white font-weight-bold text-capitalize px-7 border-0"
            elevation="2"
            style="
              height: 40px;
              background: linear-gradient(135deg, #3a67b1 0%, #0097d7 100%);
              color: #ffffff !important;
            "
            @click="closeDialog"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import common from "@/view/Common.vue";

export default {
  name: "SurveyResultPreview",
  mixins: [common],
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    previewItem: {
      type: Object,
      default: () => null,
    },
    questions: {
      type: Array,
      default: () => [],
    },
    fileBaseUrl: {
      type: String,
      default: "",
    },
  },
  computed: {
    showDialog: {
      get() {
        return this.value;
      },
      set(val) {
        console.log("showDialog val=" + val);
        this.$emit("input", val);
      },
    },
  },
  methods: {
    // Close preview modal dialog
    closeDialog() {
      console.log("closeDialog called");
      this.$emit("hideDialog");
      this.$emit("input", false);
    },
    // Check if question is file upload type (5 or 12)
    isFileType(questionType) {
      return questionType == 5 || questionType == 12;
    },
    // Format question index to 2 digits
    formatIndex(num) {
      return num < 10 ? "0" + num : num;
    },
    // Get member name initials
    getInitials(name) {
      if (!name) return "U";
      var parts = name.trim().split(" ");
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    },
    // Check if answer is short text
    isShortAnswer(val) {
      if (!val) return false;
      var str = String(val).trim();
      return str.length <= 25 && !str.includes("\n");
    },
  },
};
</script>

<style lang="scss" scoped>
.rounded-24 {
  border-radius: 24px !important;
}

.rounded-20 {
  border-radius: 20px !important;
}

.rounded-16 {
  border-radius: 16px !important;
}

.modal-header {
  background: linear-gradient(135deg, #3a67b1 0%, #0097d7 100%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
}

.header-icon-box {
  width: 36px;
  height: 36px;
  border-radius: 12px;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.close-btn {
  background: rgba(255, 255, 255, 0.15) !important;
  transition: all 0.2s ease;
  &:hover {
    background: rgba(255, 255, 255, 0.3) !important;
  }
}

.modal-body {
  background-color: #f4f7fc;
  max-height: 75vh;
}

.respondent-hero-card {
  background: linear-gradient(135deg, #3a67b1 0%, #0097d7 100%);
  box-shadow: 0 10px 30px rgba(58, 103, 177, 0.25);
}

.hero-bg-accent {
  position: absolute;
  top: -40px;
  right: -40px;
  width: 160px;
  height: 160px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(237, 190, 56, 0.3) 0%,
    rgba(255, 255, 255, 0) 70%
  );
  pointer-events: none;
}

.avatar-ring {
  padding: 3px;
  border-radius: 50%;
  background: linear-gradient(135deg, #edbe38 0%, #56bda3 100%);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.15);
}

.hero-subtext {
  font-size: 0.82rem;
  color: #ffffff;
}

.time-badge {
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.25);
}

.section-indicator {
  width: 4px;
  height: 16px;
  border-radius: 2px;
  background: linear-gradient(180deg, #3a67b1 0%, #0097d7 100%);
  display: inline-block;
}

.border-navy {
  border: 1px solid #3a67b1 !important;
}

.timeline-feed {
  position: relative;
  &::before {
    content: "";
    position: absolute;
    top: 15px;
    bottom: 25px;
    left: 17px;
    width: 2px;
    background: linear-gradient(180deg, #3a67b1 0%, #0097d7 100%);
    opacity: 0.25;
    z-index: 1;
  }
}

.timeline-item {
  z-index: 2;
}

.timeline-node {
  position: absolute;
  left: 0;
  top: 14px;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3a67b1 0%, #0097d7 100%);
  color: #ffffff;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 10px rgba(58, 103, 177, 0.3);
  z-index: 3;
}

.qa-card {
  border-color: #d5e4f7 !important;
  box-shadow: 0 2px 8px rgba(10, 15, 41, 0.04) !important;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  &:hover {
    box-shadow: 0 6px 20px rgba(10, 15, 41, 0.08) !important;
  }
}

.question-text {
  font-size: 0.93rem;
  color: #0a0f29;
  line-height: 1.45;
}

.text-answer-card {
  background-color: #f4f7fc;
  border: 1px solid #d5e4f7;
  position: relative;
  overflow: hidden;
  border-left: 4px solid #3a67b1 !important;
}

.quote-mark {
  position: absolute;
  right: 12px;
  bottom: -10px;
  font-size: 3.5rem;
  font-weight: 900;
  color: rgba(58, 103, 177, 0.08);
  line-height: 1;
  pointer-events: none;
  font-family: Georgia, serif;
}

.answer-body-text {
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 0.88rem;
  line-height: 1.6;
  color: #414042;
  position: relative;
  z-index: 2;
}

.short-answer-pill {
  background-color: #ebf4fb;
  border: 1px solid #3a67b1;
}

.file-download-card {
  background-color: #ffffff;
  border-color: #d5e4f7 !important;
}

.file-icon-box {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background-color: #ebf4fb;
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-answer-box {
  background-color: #f4f7fc;
  border: 1px dashed #3a67b1;
  display: inline-flex;
  align-items: center;
}
</style>
